// Completeness checks for an uploaded master solution / grading scheme.
//
// After a teacher UPLOADS a master solution or grading scheme (as opposed to generating it — the
// generation path guarantees coverage for every task itself), we verify that every task the grader
// will actually grade got the information it needs. A task "needs an answer" exactly when the grader
// would grade it: `needsAnswer !== false` (see default-grading.walkTask — an unset flag still means
// answerable). Task frames (`needsAnswer === false`) are skipped; they carry no own answer.
//
// Required information per answerable task:
//  - MASTER SOLUTION: a bounding box OR text content. A box may be a pure image (drawing/sketch) with no
//    transcribable text; conversely a text-only master solution keeps NO boxes at all — stripTextFromBoxes
//    (build-exam-tasks) removes every text box, leaving the transcript only in `content`. So either signal
//    (a surviving image box or non-empty content) means the master solution is present for the task.
//  - GRADING SCHEME: a bounding box OR criteria text (same box-stripping caveat), AND points. Points may
//    have been stated earlier on the exam sheet (declaredMaxPoints), not only in the grading-scheme
//    document, so "has points" is read through the points model (ownMaxPoints), which considers both the
//    scheme and the sheet.
//
// The source is ALWAYS the database (the persisted exam.tasks tree), never the just-extracted result:
// points in particular can come from an earlier step, so only the merged DB state is authoritative.

import { getCollection } from '@adornis/baseql/server/collections.js';
import { GRADING_THRESHOLDS } from '../../shared/constants.js';
import { GRAMMAR_TASK_TYPES } from '../../shared/grammar-grading.js';
import { Exam } from '../exam.js';
import { ownMaxPoints } from './points-model.js';

/** Raw DB exam-task node (recursive `subTasks`). Loose to match the raw document shape. */
type TaskNode = {
  taskNumber?: string | null;
  needsAnswer?: boolean | null;
  masterSolution?: any;
  gradingScheme?: any;
  subTasks?: TaskNode[] | null;
  [key: string]: any;
};

export interface CompletenessResult {
  complete: boolean;
  /** Display task paths (e.g. "2.a.i") of answerable tasks that are missing required information. */
  missingTaskPaths: string[];
  /** Subset of missingTaskPaths where criteria/box already exist but own points are <= 0 — the only fix
   *  is setting points in the canvas (generation skips tasks that already have criteria, so it can't help). */
  zeroPointsTaskPaths?: string[];
  /** Subset of missingTaskPaths where criteria/box are absent but own points are ALREADY > 0 — the
   *  precise complement of zeroPointsTaskPaths, so a caller can say "only criteria is missing" instead of
   *  the vague "criteria and/or points" when points are demonstrably already there. */
  missingCriteriaOnlyTaskPaths?: string[];
}

/** Embedded objects are stored plain, but some read paths wrap them as `{ doc }`. */
function unwrap(value: any): any {
  return value?.doc ?? value;
}

/** True if the given section (masterSolution / gradingScheme) carries at least one bounding box or
 *  non-empty text content. Text-only regions survive only as content (their boxes are stripped), image
 *  regions survive only as boxes (no content) — either is enough to count the section as present.
 *  `contentField` is `content` for a master solution, `criteria` for a grading scheme. */
function hasSectionContent(section: any, contentField: 'content' | 'criteria'): boolean {
  const s = unwrap(section);
  const boxes = s?.boundingBoxes;
  if (Array.isArray(boxes) && boxes.length > 0) return true;
  const text = s?.[contentField];
  return typeof text === 'string' && text.trim().length > 0;
}

/** A task needs an answer (and thus gets graded) unless it is explicitly marked as a task frame. */
function needsAnswer(node: TaskNode): boolean {
  return node?.needsAnswer !== false;
}

/**
 * Every answerable task in the tree, flattened. Exported so anything else that has to reason about "the
 * tasks that get graded" (provenance roll-up, for one) uses the same rule rather than its own copy of it.
 */
export function collectAnswerableTasks(tasks: TaskNode[] | null | undefined): TaskNode[] {
  const out: TaskNode[] = [];
  const walk = (nodes: TaskNode[] | null | undefined): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node) continue;
      if (needsAnswer(node)) out.push(node);
      walk(node.subTasks);
    }
  };
  walk(tasks);
  return out;
}

/**
 * Walks the task tree and collects the display paths of every answerable task for which `isMissing`
 * reports the required information is absent.
 */
function collectMissing(tasks: TaskNode[] | null | undefined, isMissing: (node: TaskNode) => boolean): string[] {
  const missing: string[] = [];
  const walk = (nodes: TaskNode[] | null | undefined, parentPath: string): void => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node, index) => {
      if (!node) return;
      const tNum = String(node.taskNumber ?? index + 1);
      const path = parentPath ? `${parentPath}.${tNum}` : tNum;
      if (needsAnswer(node) && isMissing(node)) missing.push(path);
      walk(node.subTasks, path);
    });
  };
  walk(tasks, '');
  return missing;
}

/**
 * A master solution is complete when every answerable task has a bounding box or text content.
 */
export function evaluateMasterSolutionComplete(tasks: TaskNode[] | null | undefined): CompletenessResult {
  const missingTaskPaths = collectMissing(tasks, node => !hasSectionContent(node.masterSolution, 'content'));
  return { complete: missingTaskPaths.length === 0, missingTaskPaths };
}

/**
 * A grading scheme is complete when every answerable task has a bounding box or criteria text AND points
 * (own points > 0, read from the points model so exam-sheet points count too).
 */
export function evaluateGradingSchemeComplete(tasks: TaskNode[] | null | undefined): CompletenessResult {
  const missingTaskPaths = collectMissing(
    tasks,
    node => !hasSectionContent(node.gradingScheme, 'criteria') || ownMaxPoints(node as any) <= 0,
  );
  const zeroPointsTaskPaths = collectMissing(
    tasks,
    node => hasSectionContent(node.gradingScheme, 'criteria') && ownMaxPoints(node as any) <= 0,
  );
  const missingCriteriaOnlyTaskPaths = collectMissing(
    tasks,
    node => !hasSectionContent(node.gradingScheme, 'criteria') && ownMaxPoints(node as any) > 0,
  );
  return {
    complete: missingTaskPaths.length === 0,
    missingTaskPaths,
    zeroPointsTaskPaths,
    missingCriteriaOnlyTaskPaths,
  };
  return {
    complete: missingTaskPaths.length === 0,
    missingTaskPaths,
    zeroPointsTaskPaths,
    missingCriteriaOnlyTaskPaths,
  };
}

/**
 * Answerable tasks that carry no grading-scheme SECTION at all — box or criteria, points ignored.
 *
 * This is the question "is there anything the generation could still fill?", which is not the same as
 * `evaluateGradingSchemeComplete`: that one also demands points, and a task that has criteria but no points
 * is something the generation cannot fix (it skips tasks that already have criteria rather than overwriting
 * them). Missing points are completed in the canvas, so they must not keep the generation tool on offer.
 */
export function evaluateGradingSchemeSectionComplete(tasks: TaskNode[] | null | undefined): CompletenessResult {
  const missingTaskPaths = collectMissing(tasks, node => !hasSectionContent(node.gradingScheme, 'criteria'));
  return { complete: missingTaskPaths.length === 0, missingTaskPaths };
}

/** Whether this task already has a language-use rubric — membership in an exam-level LanguageUseScheme
 * entry (`languageUseSchemeRules[].dependsOn`, matched by the same "|"-joined qualified path
 * build-exam-tasks.ts and the AI generation agent use), solo (1-member) or multi-member alike. Duplicated
 * from hasExistingGrammarContent in gs-generation-agent.ts rather than imported — that module pulls in
 * server-only LLM/agent dependencies this completeness check must stay free of. */
function hasLanguageUseSectionContent(qualifiedPath: string, languageUseSchemeRules: any[]): boolean {
  return languageUseSchemeRules.some(
    scheme => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.includes(qualifiedPath),
  );
}

/**
 * A grammar-eligible answerable task (ESSAY, MATERIAL_BASED_ESSAY, LITERARY_ANALYSIS_POETRY,
 * ANALYSIS_NON_FICTION — see shared/grammar-grading.ts) is missing its language-use rubric when it has no
 * LanguageUseScheme covering it (solo or multi-member). Mirrors
 * evaluateGradingSchemeSectionComplete's own two exclusions, applied to the language-use channel: a task
 * with no content criteria yet is already counted by that check, so it's excluded here too (nothing new to
 * report); and a task with content but no usable OWN points is excluded the same way content-completeness
 * excludes missing points — there is no basis to compute a language-use share for it, so it must not
 * permanently block completeness.
 *
 * ALSO flags — via zeroPointsTaskPaths, folded into missingTaskPaths so the gate actually blocks on it —
 * a LanguageUseScheme that EXISTS (solo or multi-member) but has maxPoints <= 0, mirroring
 * evaluateGradingSchemeComplete's content-side zero-points check. This is checked regardless of the
 * task-type eligibility gate above: the data exists and is broken, which is a different question from
 * "is this task even supposed to have one". A zero-points rule blocks every one of its dependents at
 * once, computed once up front rather than per task.
 */
export function evaluateLanguageUseSchemeSectionComplete(
  tasks: TaskNode[] | null | undefined,
  languageUseSchemeRules: any[] | null | undefined,
): CompletenessResult {
  const rules = Array.isArray(languageUseSchemeRules) ? languageUseSchemeRules : [];
  const missing: string[] = [];
  const zeroPoints: string[] = [];

  const zeroPointsGroupPaths = new Set<string>();
  for (const rule of rules) {
    const criteria = typeof rule?.criteria === 'string' ? rule.criteria.trim() : '';
    const maxPoints = typeof rule?.maxPoints === 'number' ? rule.maxPoints : 0;
    if (!criteria || maxPoints > 0) continue;
    for (const dep of Array.isArray(rule?.dependsOn) ? rule.dependsOn : []) {
      zeroPointsGroupPaths.add(String(dep));
    }
  }

  const walk = (nodes: TaskNode[] | null | undefined, parentPath: string[], parentDisplay: string): void => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node, index) => {
      if (!node) return;
      const tNum = String(node.taskNumber ?? index + 1);
      const path = [...parentPath, tNum];
      const displayPath = parentDisplay ? `${parentDisplay}.${tNum}` : tNum;
      const qualifiedPath = path.join('|');
      const taskType = typeof node.taskType === 'string' ? node.taskType : undefined;
      const hasUsablePoints = typeof unwrap(node.gradingScheme)?.maxPoints === 'number';
      const groupZeroPoints = zeroPointsGroupPaths.has(qualifiedPath);

      if (needsAnswer(node) && groupZeroPoints) {
        zeroPoints.push(displayPath);
      } else if (
        needsAnswer(node) &&
        taskType &&
        GRAMMAR_TASK_TYPES.has(taskType) &&
        hasSectionContent(node.gradingScheme, 'criteria') &&
        hasUsablePoints &&
        !hasLanguageUseSectionContent(qualifiedPath, rules)
      ) {
        missing.push(displayPath);
      }
      walk(node.subTasks, path, displayPath);
    });
  };
  walk(tasks, [], '');
  const missingTaskPaths = [...missing, ...zeroPoints];
  return { complete: missingTaskPaths.length === 0, missingTaskPaths, zeroPointsTaskPaths: zeroPoints };
}

/**
 * Every distinct grading-scheme review ITEM: an answerable-task path with a raw low-confidence OCR
 * region, zero/negative own points (content or language-use), or a stored confidence below threshold
 * (content or own-merged language-use). A task tripping several of its OWN signals at once (e.g. zero points
 * AND a low-confidence box) is counted once, since all of those live under the same task-path key.
 *
 * A flagged shared LanguageUseScheme group is folded in the SAME way, not counted separately: if ANY one of
 * its dependent tasks is already in the set for its own reasons, the group adds nothing — that task is
 * already surfacing a review action, and the group rides along with it rather than getting its own
 * separate item. Only when NONE of its dependents have any other issue does the group contribute its own
 * single item (keyed by the group's id, not by task, so it still doesn't scale with the number of
 * dependents). This matches the canvas's own uncertaintyTaskIndices exactly: it marks a group's id as
 * "seen" the moment it visits ANY dependent task (whether that task was counted for the group or for its
 * own reason), so a later dependent with no issue of its own never gets an extra count once some earlier
 * dependent already claimed the group.
 *
 * Zero points is a completeness problem — flagged regardless of isApproved, since approving doesn't
 * fix a task's inability to be graded (the backend gate ignores isApproved too). A plain low-confidence
 * signal is NOT a completeness problem, so it IS resolved by isApproved, exactly like the canvas UI
 * (scalarToStatus's own `task.gradingSchemeIsApproved ? 'ok' : ...`) — conflating the two here would
 * mean an approved-but-naturally-low-confidence task can never leave this count.
 */
export function collectGradingSchemeUncertainTaskPaths(
  tasks: TaskNode[] | null | undefined,
  languageUseSchemeRules: any[] | null | undefined,
  lowConfRegions: Array<{ taskPath?: string[] | null }> | null | undefined,
): Set<string> {
  const keys = new Set<string>();

  for (const region of Array.isArray(lowConfRegions) ? lowConfRegions : []) {
    const path = Array.isArray(region?.taskPath) ? region.taskPath.filter(Boolean).join('.') : '';
    if (path) keys.add(path);
  }

  evaluateGradingSchemeComplete(tasks).zeroPointsTaskPaths?.forEach(path => keys.add(path));

  collectMissing(tasks, node => {
    const gs = unwrap(node.gradingScheme);
    if (!gs) return false;
    if (!gs.isApproved && typeof gs.confidence === 'number' && gs.confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE)
      return true;
    return false;
  }).forEach(path => keys.add(path));

  // Per-task signals are fully collected above BEFORE language-use rules are considered — every
  // language-use rule (solo or multi-member) is a LanguageUseScheme now, so its own contribution (zero
  // points / low confidence) is decided uniformly below, by whether it would add anything new on top of
  // the set collected so far.
  for (const rule of Array.isArray(languageUseSchemeRules) ? languageUseSchemeRules : []) {
    const criteria = typeof rule?.criteria === 'string' ? rule.criteria.trim() : '';
    if (!criteria) continue;
    const maxPoints = typeof rule?.maxPoints === 'number' ? rule.maxPoints : 0;
    const confidence = typeof rule?.confidence === 'number' ? rule.confidence : undefined;
    const zeroPoints = maxPoints <= 0;
    const lowConfidence =
      !rule?.isApproved && confidence !== undefined && confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;
    const flagged = zeroPoints || lowConfidence;
    if (!flagged || !rule?._id) continue;

    const dependsOn: string[] = Array.isArray(rule?.dependsOn) ? rule.dependsOn : [];
    const dependentPaths = dependsOn.map((dep: unknown) => String(dep).split('|').filter(Boolean).join('.'));
    const anyDependentAlreadyFlagged = dependentPaths.some(path => keys.has(path));
    if (!anyDependentAlreadyFlagged) keys.add(`group:${rule._id}`);
  }

  return keys;
}

/**
 * True as soon as ONE answerable task carries a grading-scheme section — i.e. "a grading scheme exists at
 * all", regardless of coverage. The progress state cannot answer this: extraction and generation share it,
 * so a generation filling gaps puts it back to IN_PROGRESS while a scheme is sitting in the tasks.
 */
export function hasAnyGradingSchemeContent(tasks: TaskNode[] | null | undefined): boolean {
  return collectAnswerableTasks(tasks).some(node => hasSectionContent(node.gradingScheme, 'criteria'));
}

/** Loads the exam's persisted task tree from the database. */
async function loadTasks(examId: string): Promise<TaskNode[]> {
  if (!examId) return [];
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } } as any);
  return Array.isArray((exam as any)?.tasks) ? ((exam as any).tasks as TaskNode[]) : [];
}

/** Loads the exam's persisted task tree AND exam-level language-use-scheme groups — the two inputs
 *  evaluateLanguageUseSchemeSectionComplete needs to resolve both own-task and group language-use coverage. */
async function loadTasksAndLanguageUseSchemeRules(
  examId: string,
): Promise<{ tasks: TaskNode[]; languageUseSchemeRules: any[] }> {
  if (!examId) return { tasks: [], languageUseSchemeRules: [] };
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne(
    { _id: examId } as any,
    { projection: { tasks: 1, languageUseSchemeRules: 1 } } as any,
  );
  return {
    tasks: Array.isArray((exam as any)?.tasks) ? ((exam as any).tasks as TaskNode[]) : [],
    languageUseSchemeRules: Array.isArray((exam as any)?.languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [],
  };
}

/** Checks the uploaded master solution against the current database state. */
export async function checkMasterSolutionComplete(examId: string): Promise<CompletenessResult> {
  return evaluateMasterSolutionComplete(await loadTasks(examId));
}

/** Checks the uploaded grading scheme against the current database state. */
export async function checkGradingSchemeComplete(examId: string): Promise<CompletenessResult> {
  return evaluateGradingSchemeComplete(await loadTasks(examId));
}

/** Checks the language-use rubric (own or group) against the current database state. */
export async function checkLanguageUseSchemeComplete(examId: string): Promise<CompletenessResult> {
  const { tasks, languageUseSchemeRules } = await loadTasksAndLanguageUseSchemeRules(examId);
  return evaluateLanguageUseSchemeSectionComplete(tasks, languageUseSchemeRules);
}
